import * as React from 'react'
import {
  List,
  DatagridConfigurable,
  TextField,
  DateField,
  FunctionField,
  FilterButton,
  TopToolbar,
  SelectColumnsButton,
  TextInput,
  SelectInput,
  Link,
} from 'react-admin'
import { Box, Chip } from '@mui/material'

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'success' },
  redeemed: { label: 'Redeemed', color: 'primary' },
  pending: { label: 'Pending', color: 'warning' },
  failed: { label: 'Failed', color: 'error' },
}

// Display-only mirror of util/constants.js replacementCouponReasons. The dialog that
// issues a coupon takes its choices from GET /coupons/preflight, but a list column
// cannot wait on a per-row fetch — an unknown code falls through to the raw value, so a
// reason added server-side shows up here as its code rather than disappearing.
const REASON_LABELS = {
  freeSecondTest: 'Free second test (provider authorized)',
  kitLost: 'Kit lost in transit',
  kitDamaged: 'Kit damaged / sample issue',
  other: 'Other',
}

const filters = [
  <TextInput label="Search code / email / kit" source="q" alwaysOn sx={{ width: '280px' }} resettable />,
  <SelectInput
    label="Status"
    source="status"
    choices={[
      { id: 'active', name: 'Active' },
      { id: 'redeemed', name: 'Redeemed' },
      { id: 'pending', name: 'Pending' },
      { id: 'failed', name: 'Failed' },
    ]}
  />,
  <SelectInput
    label="Reason"
    source="reasonCode"
    choices={Object.entries(REASON_LABELS).map(([id, name]) => ({ id, name }))}
  />,
]

const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <SelectColumnsButton />
    {/* No CreateButton: coupons are only ever issued from a kit, via the
        "Create Coupon Code" button on the Notify Complete / Error kit lists. */}
  </TopToolbar>
)

const CouponList = (props) => {
  return (
    <Box>
      <List {...props} filters={filters} actions={<ListActions />}>
        <DatagridConfigurable bulkActionButtons={false}>
          <TextField source="couponCode" label="Coupon Code" />
          <FunctionField
            label="Status"
            source="status"
            render={(record) => {
              const config = STATUS_CONFIG[record.status] || { label: record.status, color: 'default' }
              return <Chip size="small" label={config.label} color={config.color} />
            }}
          />
          <TextField source="customerName" label="Customer Name" />
          <TextField source="customerEmail" label="Customer Email" />
          <TextField source="sku" label="SKU" />
          <FunctionField
            label="Reason"
            source="reasonCode"
            render={(record) => (record.reasonCode ? REASON_LABELS[record.reasonCode] || record.reasonCode : '—')}
          />
          <FunctionField
            label="Kit Id"
            source="kitId"
            render={(record) => (record.kitId ? <Link to={`/kits/${record.kitId}/show`}>{record.kitId}</Link> : '—')}
          />
          <TextField source="kitShopifyOrderNumber" label="Order Num" emptyText="—" />
          <DateField source="issuedAt" label="Generated" showTime sortable={false} />
          <DateField source="expiresAt" label="Expires" />
          {/* The order the customer placed with this code. Stamped by orderSync, so it
              only appears once the order has been pulled back from Shopify. */}
          <FunctionField
            label="Replacement Order"
            source="redeemedOrderId"
            render={(record) =>
              record.redeemedOrderId ? (
                <Link to={`/orders/${record.redeemedOrderId}/show`}>
                  {record.redeemedShopifyOrderNumber || record.redeemedOrderId}
                </Link>
              ) : (
                '—'
              )
            }
          />
          <DateField source="redeemedAt" label="Redeemed At" showTime emptyText="—" />
          {/* The other half of the backtracking chain: which kit this coupon produced. */}
          <FunctionField
            label="Replacement Kit"
            source="replacementKitId"
            render={(record) =>
              record.replacementKitId ? <Link to={`/kits/${record.replacementKitId}/show`}>{record.replacementKitId}</Link> : '—'
            }
          />
        </DatagridConfigurable>
      </List>
    </Box>
  )
}

export default CouponList
